// packages/web/src/components/admin/email-templates/template-edit-form.tsx
import { useForm } from "@tanstack/react-form";
import { z } from "zod";
import { toast } from "sonner";
import { Field, FieldLabel, FieldError, FieldGroup } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useUpsertEmailTemplate } from "@/api/email-templates/mutations";

interface LocaleRow {
  locale: string;
  subject: string;
  slots: Record<string, string>;
}

interface Props {
  templateKey: string;
  locale: string;
  slotKeys: string[];
  variableKeys: string[];
  defaultSubject: string;
  defaultSlots: Record<string, string>;
  existingRow: LocaleRow | undefined;
  onDraftChange: (draft: { subject: string; slots: Record<string, string> }) => void;
}

const formSchema = z.object({
  subject: z.string().min(1, "Onderwerp is verplicht").max(200),
  slots: z.record(z.string(), z.string().max(4000)),
});

function humanize(key: string): string {
  return key.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
}

export function TemplateEditForm(props: Props) {
  const upsert = useUpsertEmailTemplate();

  const initial = props.existingRow ?? {
    locale: props.locale,
    subject: props.defaultSubject,
    slots: props.defaultSlots,
  };

  const form = useForm({
    defaultValues: {
      subject: initial.subject,
      slots: { ...props.defaultSlots, ...initial.slots },
    },
    validators: { onChange: formSchema },
    onSubmit: async ({ value }) => {
      try {
        await upsert.mutateAsync({
          key: props.templateKey,
          locale: props.locale,
          body: { subject: value.subject, slots: value.slots },
        });
        toast.success("Sjabloon opgeslagen");
      } catch (err) {
        toast.error((err as Error).message);
      }
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void form.handleSubmit();
      }}
      onChange={() => props.onDraftChange(form.state.values)}
    >
      <FieldGroup>
        <form.Field name="subject">
          {(field) => {
            const err = field.state.meta.errors[0];
            const errMsg = typeof err === "string" ? err : err?.message;
            const showErr = field.state.meta.isTouched && !field.state.meta.isValid;
            return (
              <Field>
                <FieldLabel htmlFor="subject">Onderwerp</FieldLabel>
                <Input
                  id="subject"
                  value={field.state.value}
                  onChange={(e) => field.handleChange(e.currentTarget.value)}
                  onBlur={field.handleBlur}
                  aria-invalid={showErr ? "true" : undefined}
                />
                {showErr && errMsg && <FieldError>{errMsg}</FieldError>}
              </Field>
            );
          }}
        </form.Field>

        {props.slotKeys.map((slotKey) => (
          <form.Field key={slotKey} name={`slots.${slotKey}` as `slots.${string}`}>
            {(field) => {
              const err = field.state.meta.errors[0];
              const errMsg = typeof err === "string" ? err : err?.message;
              const showErr = field.state.meta.isTouched && !field.state.meta.isValid;
              return (
                <Field>
                  <FieldLabel htmlFor={`slot-${slotKey}`}>{humanize(slotKey)}</FieldLabel>
                  <Textarea
                    id={`slot-${slotKey}`}
                    value={(field.state.value as string) ?? ""}
                    onChange={(e) => field.handleChange(e.currentTarget.value)}
                    onBlur={field.handleBlur}
                    rows={3}
                    aria-invalid={showErr ? "true" : undefined}
                  />
                  {props.variableKeys.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {props.variableKeys.map((v) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => {
                            const ta = document.getElementById(
                              `slot-${slotKey}`,
                            ) as HTMLTextAreaElement | null;
                            if (!ta) return;
                            const start = ta.selectionStart ?? ta.value.length;
                            const end = ta.selectionEnd ?? ta.value.length;
                            const next =
                              ta.value.slice(0, start) + `{{${v}}}` + ta.value.slice(end);
                            field.handleChange(next);
                          }}
                        >
                          <Badge>{`{{${v}}}`}</Badge>
                        </button>
                      ))}
                    </div>
                  )}
                  {showErr && errMsg && <FieldError>{errMsg}</FieldError>}
                </Field>
              );
            }}
          </form.Field>
        ))}
      </FieldGroup>

      <div className="mt-6 flex gap-2">
        <Button type="submit" variant="primary" disabled={upsert.isPending}>
          Opslaan
        </Button>
      </div>
    </form>
  );
}
